import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

/* ════════════════════════════════════════════
   SCROLL TO TOP ON ROUTE CHANGE
   ════════════════════════════════════════════ */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ════════════════════════════════════════════
   SEO COMPONENT
   ════════════════════════════════════════════ */
function SEO({ title, description, keywords }) {
  const defaultKeywords = "Palliative Care and Cancer Care in Tenkasi, Cancer Palliative Care, Tirunelveli and Madurai Palliative Care, home based palliative care, cancer care NGO, Vidiyal Medical Mission, pain management, hospice care, geriatric care, medical camps, health education, palliative care training, cancer support India, free cancer care";
  return (
    <Helmet>
      <title>{title || 'Vidiyal Medical Mission - Leaders in Home Based Palliative Care'}</title>
      <meta name="description" content={description || 'Vidiyal Medical Mission provides free home-based palliative care, counseling, helpline support & training for cancer patients in Tenkasi, Tirunelveli, and Madurai. Leaders in Cancer Palliative Care in South India.'} />
      <meta name="keywords" content={keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords} />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content={title || 'Vidiyal Medical Mission'} />
      <meta property="og:description" content={description || 'Free home-based palliative care for cancer patients in Tenkasi, Tirunelveli, and Madurai.'} />
      <meta property="og:type" content="website" />
      <meta name="geo.region" content="IN-TN" />
      <meta name="geo.placename" content="Tenkasi, Tamil Nadu" />
      <link rel="canonical" href="https://vidiyalmedicalmission.org" />
    </Helmet>
  );
}

/* ════════════════════════════════════════════
   TOP BAR
   ════════════════════════════════════════════ */
function TopBar() {
  return (
    <div className="vm-topbar">
      <div className="vm-topbar__inner">
        <div className="vm-topbar__left">
          <span><i className="fas fa-envelope"></i> 279, Amman Sannathi 2nd St, Tenkasi - 627811</span>
          <span><i className="fas fa-phone"></i> Helpline: +91 9486849191, 9894848252</span>
        </div>
        <div className="vm-topbar__right">
          <a href="#" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
          <a href="#" aria-label="Twitter"><i className="fab fa-x-twitter"></i></a>
          <a href="#" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
          <a href="#" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
          <a href="#" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   HEADER / NAVIGATION
   ════════════════════════════════════════════ */
function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navRef = useRef(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    document.body.style.overflow = '';
  }, []);

  const toggleMenu = () => {
    setMenuOpen(prev => {
      document.body.style.overflow = !prev ? 'hidden' : '';
      return !prev;
    });
  };

  const toggleDropdown = (name, e) => {
    if (window.innerWidth <= 768) {
      e.preventDefault();
      setActiveDropdown(prev => prev === name ? null : name);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) closeMenu();
    };
    const handleResize = () => { if (window.innerWidth > 768) closeMenu(); };
    const handleKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKey);
    };
  }, [closeMenu]);

  return (
    <header className="vm-header" ref={navRef}>
      <div className="vm-header__brand">
        <Link to="/" onClick={closeMenu}>
          <img src={`${process.env.PUBLIC_URL}/images/VidiyalMissionLogo.jpeg`} alt="Vidiyal Medical Mission Logo" className="vm-header__logo" />
        </Link>
        <div className="vm-header__taglines">
          <h1 className="vm-header__tagline">LEADERS IN HOME BASED<br />PAIN &amp; PALLIATIVE CARE</h1>
          <p className="vm-header__since">Caring for Cancer Patients since 2016</p>
        </div>
      </div>

      <div className="vm-header__nav-row">
        <nav className={`vm-nav ${menuOpen ? 'vm-nav--open' : ''}`}>
          <ul className="vm-nav__list">
            <li><Link to="/" onClick={closeMenu}>Home</Link></li>

            <li className={`vm-nav__dropdown ${activeDropdown === 'who' ? 'vm-nav__dropdown--open' : ''}`}>
              <a href="#" onClick={(e) => toggleDropdown('who', e)}>Who We Are <i className="fas fa-chevron-down"></i></a>
              <ul className="vm-nav__sub">
                <li><Link to="/teams" onClick={closeMenu}>Team</Link></li>
                <li><a href="#contact" onClick={closeMenu}>Contact Us</a></li>
              </ul>
            </li>

            <li className={`vm-nav__dropdown ${activeDropdown === 'programs' ? 'vm-nav__dropdown--open' : ''}`}>
              <a href="#" onClick={(e) => toggleDropdown('programs', e)}>Programs <i className="fas fa-chevron-down"></i></a>
              <ul className="vm-nav__sub">
                <li><Link to="/palliative-care" onClick={closeMenu}>Pain &amp; Palliative Care</Link></li>
                <li><Link to="/hospice-geriatric-care" onClick={closeMenu}>Home Care, Hospice &amp; Geriatric Care</Link></li>
                <li><Link to="/medical-camps" onClick={closeMenu}>Medical Camps &amp; Treatment Navigation</Link></li>
                <li><Link to="/health-education" onClick={closeMenu}>Health Education, Training &amp; Awareness</Link></li>
                <li><a href="tel:+919894848252" onClick={closeMenu}>Telephone Helpline: 9894848252</a></li>
              </ul>
            </li>

            <li className={`vm-nav__dropdown ${activeDropdown === 'learn' ? 'vm-nav__dropdown--open' : ''}`}>
              <a href="#" onClick={(e) => toggleDropdown('learn', e)}>Learn with Us <i className="fas fa-chevron-down"></i></a>
              <ul className="vm-nav__sub">
                <li><a href="#training" onClick={closeMenu}>Palliative Care Training</a></li>
              </ul>
            </li>

            <li><a href="#get-involved" onClick={closeMenu}>Get Involved</a></li>

            <li className={`vm-nav__dropdown ${activeDropdown === 'resources' ? 'vm-nav__dropdown--open' : ''}`}>
              <a href="#" onClick={(e) => toggleDropdown('resources', e)}>Resources <i className="fas fa-chevron-down"></i></a>
              <ul className="vm-nav__sub">
                <li><a href="#stories" onClick={closeMenu}>Patient Care Stories</a></li>
                <li><a href="#gallery" onClick={closeMenu}>Gallery</a></li>
              </ul>
            </li>
          </ul>
        </nav>

        <Link to="/donations" className="vm-btn vm-btn--donate vm-btn--flash" onClick={closeMenu}>Donate Now</Link>

        <button className={`vm-hamburger ${menuOpen ? 'vm-hamburger--open' : ''}`} onClick={toggleMenu} aria-label="Toggle menu">
          <span></span><span></span><span></span>
        </button>
      </div>
    </header>
  );
}

/* ════════════════════════════════════════════
   FOOTER
   ════════════════════════════════════════════ */
function Footer() {
  return (
    <>
      <div className="vm-infobar">
        <p>Donations to the Trust are exempted under 12-Sub-clause (A) of clause (iv) of first proviso to sub section (5) of section 80G.</p>
        <p><strong>Indian Individuals:</strong> Your donation is 50% tax exempt under Section 80G of the IT Act.</p>
      </div>
      <footer className="vm-footer">
        <div className="vm-footer__grid">
          <div className="vm-footer__col">
            <img src={`${process.env.PUBLIC_URL}/images/VidiyalMissionLogo.jpeg`} alt="Vidiyal Mission Logo" className="vm-footer__logo" />
            <h4>LEADERS IN HOME BASED<br />PALLIATIVE CARE</h4>
            <p className="vm-footer__tagline">Caring for Cancer Patients since 2016</p>
            <p>We work towards a caring and supportive society where people with cancer and their families live with dignity, hope and comfort.</p>
          </div>
          <div className="vm-footer__col">
            <h3>Quick Links</h3>
            <ul>
              <li><Link to="/teams">Team</Link></li>
              <li><a href="#contact">Contact Us</a></li>
              <li><Link to="/donations">Your Donations</Link></li>
            </ul>
          </div>
          <div className="vm-footer__col">
            <h3>Address</h3>
            <p><strong>Vidiyal Mission</strong></p>
            <p>279, Amman Sannathi 2nd St,<br />Near Kasiviswanathar Temple,<br />Tenkasi, South India - 627811</p>
            <br />
            <p>7/6A Kamaraj 1st Street,<br />Near IRT Polytechnic,<br />Nagercoil Road, Palayamkottai,<br />Tirunelveli - 627007, South India.</p>
            <h3 className="vm-footer__follow">Follow Us</h3>
            <div className="vm-footer__social">
              <a href="#"><i className="fab fa-facebook-f"></i></a>
              <a href="#"><i className="fab fa-x-twitter"></i></a>
              <a href="#"><i className="fab fa-linkedin-in"></i></a>
              <a href="#"><i className="fab fa-youtube"></i></a>
              <a href="#"><i className="fab fa-instagram"></i></a>
            </div>
          </div>
          <div className="vm-footer__col">
            <h3>Contact Us</h3>
            <p><strong>contactus@vidiyalmedicalmission.org</strong></p>
            <p><strong>Tenkasi</strong></p>
            <p>+91 9486849191 &nbsp;&nbsp; +91 9894848252</p>
            <p>Mon to Sat – 09:00 am to 6:00 pm</p>
          </div>
        </div>
        <div className="vm-footer__bottom">
          <span>FAQs &nbsp;|&nbsp; Terms and Conditions &nbsp;|&nbsp; Privacy Policy</span>
          <span>© Copyright 2026 VidiyalMedicalMission. All Rights Reserved.</span>
        </div>
      </footer>
    </>
  );
}

/* ════════════════════════════════════════════
   IMAGE SLIDER (AUTO-ADVANCE)
   ════════════════════════════════════════════ */
function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const slides = [
    { src: `${process.env.PUBLIC_URL}/images/scroll-banner/homecare.png`, alt: 'Home Care Services - Palliative Care in Tenkasi' },
    { src: `${process.env.PUBLIC_URL}/images/scroll-banner/palliativecare.png`, alt: 'Cancer Palliative Care Services' },
    { src: `${process.env.PUBLIC_URL}/images/scroll-banner/hospicegeriatriccare.png`, alt: 'Hospice and Geriatric Care in Tirunelveli' },
    { src: `${process.env.PUBLIC_URL}/images/scroll-banner/healtheducation.png`, alt: 'Health Education and Training' },
  ];
  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % slides.length), 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="vm-slider" aria-label="Hero banner showcasing palliative care services">
      <div className="vm-slider__track" style={{ transform: `translateX(-${current * 100}%)` }}>
        {slides.map((s, i) => (
          <div className="vm-slider__slide" key={i}>
            <img src={s.src} alt={s.alt} loading={i === 0 ? 'eager' : 'lazy'} />
          </div>
        ))}
      </div>
      <div className="vm-slider__dots">
        {slides.map((_, i) => (
          <button key={i} className={`vm-slider__dot ${i === current ? 'vm-slider__dot--active' : ''}`} onClick={() => setCurrent(i)} aria-label={`Go to slide ${i + 1}`} />
        ))}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════
   ANIMATED COUNTER
   ════════════════════════════════════════════ */
function Counter({ target, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let c = 0;
        const inc = Math.max(1, Math.ceil(target / 80));
        const timer = setInterval(() => {
          c += inc;
          if (c >= target) { c = target; clearInterval(timer); }
          setCount(c);
        }, 20);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div className="vm-counter" ref={ref}>
      <span className="vm-counter__number">{count.toLocaleString()}+</span>
      <span className="vm-counter__label">{label}</span>
    </div>
  );
}

/* ════════════════════════════════════════════
   PAGE HERO BANNER (SUB-PAGES)
   ════════════════════════════════════════════ */
function PageHero({ src, alt }) {
  return (
    <section className="vm-page-hero">
      <img src={src} alt={alt} />
    </section>
  );
}

/* ════════════════════════════════════════════
   IMAGE GALLERY GRID
   ════════════════════════════════════════════ */
function ImageGallery({ images, columns = 4 }) {
  return (
    <div className="vm-gallery" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {images.map((img, i) => (
        <div className="vm-gallery__item" key={i}>
          <img src={img.src} alt={img.alt || 'Gallery image'} loading="lazy" />
          {img.caption && <p className="vm-gallery__caption">{img.caption}</p>}
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   HOME PAGE
   ════════════════════════════════════════════════════════════ */
function HomePage() {
  return (
    <>
      <SEO
        title="Vidiyal Medical Mission - Palliative Care and Cancer Care in Tenkasi | Cancer Palliative Care"
        description="Vidiyal Medical Mission provides free home-based palliative care, counseling, helpline support & training for cancer patients in Tenkasi, Tirunelveli, and Madurai. Leading Cancer Palliative Care NGO in South India."
        keywords="Palliative Care and Cancer Care in Tenkasi, Cancer Palliative Care, Tirunelveli and Madurai Palliative Care"
      />

      <HeroSlider />

      {/* What We Do - Featured */}
      <section className="vm-whatwedo-featured">
        <div className="vm-container vm-whatwedo-featured__inner">
          <div className="vm-whatwedo-featured__img">
            <img src={`${process.env.PUBLIC_URL}/images/whatwedo.jpeg`} alt="Palliative care and cancer care support in Tenkasi" />
          </div>
          <div className="vm-whatwedo-featured__content">
            <h2>What We Do</h2>
            <p className="vm-whatwedo-featured__highlight">
              Vidiyal Mission is a team of Palliative Care experts committed to maximising quality of life for those living with cancer in a meaningful and compassionate manner, whilst fully respecting the dignity of the individual.
            </p>
            <p>We follow a patient-centered philosophy where patient preferences and goals are central to providing care.</p>
            <p>We run the home based palliative care program in South India, serving Tenkasi, Tirunelveli, and Madurai districts.</p>
            <Link to="/palliative-care" className="vm-btn vm-btn--gold">Read More</Link>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="vm-journey">
        <h2 className="vm-section-title">Journey So Far</h2>
        <div className="vm-journey__grid">
          {[
            { year: '2015', text: 'The journey began in the aftermath of the Nepal Earthquake. Amid destruction and human suffering, we stepped into relief work. What started as a response to crisis became a calling to serve.' },
            { year: '2016', text: 'Registered as a charitable trust. We began our focus on palliative care, providing comfort and compassion to cancer patients in Tenkasi.' },
            { year: '2017', text: 'Expanded services to Tirunelveli district, growing our home-based palliative care program with dedicated medical professionals.' },
            { year: '2018', text: 'Strengthened partnerships with local hospitals and clinics. Launched health education workshops for communities.' },
            { year: '2020', text: 'During the COVID-19 pandemic, adapted our services to continue providing uninterrupted care. Introduced teleconsultation services.' },
            { year: '2024', text: 'Extended our reach to Madurai. Now serving three districts with comprehensive cancer palliative care and medical camp programs.' },
          ].map((item, i) => (
            <div className="vm-journey__card" key={i}>
              <span className="vm-journey__year">{item.year}</span>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Vision / Mission / Values */}
      <section className="vm-vmv">
        {[
          { label: 'Vision', text: 'Our vision is to make accessible, respectful, and compassionate health care a reality for every life — especially for those battling cancer in Tenkasi, Tirunelveli, and Madurai.' },
          { label: 'Mission', text: 'Compassionate care at every doorstep — supporting recovery, dignity, and comfort through every stage of life, including hospice and end-of-life care.' },
          { label: 'Values', text: 'To deliver compassionate, evidence-based care through a multidisciplinary team of doctors, nurses, physiotherapists, and professionals in advisory, counselling, and IT, upholding the highest ethical standards, respecting patient rights and team contributions, ensuring transparency with donors and families, and responsibly stewarding resources to promote dignity, accessibility, and quality of life.' },
        ].map((item, i) => (
          <div className="vm-vmv__card" key={i}>
            <div className="vm-vmv__left">
              <span className="vm-vmv__our">Our</span>
              <span className="vm-vmv__label">{item.label}</span>
            </div>
            <div className="vm-vmv__right">
              <p>{item.text}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Support With Care */}
      <section className="vm-support">
        <div className="vm-container vm-support__inner">
          <div className="vm-support__text">
            <h2>Support With Care</h2>
            <p>Your generosity helps us continue providing free palliative care services to cancer patients. Every contribution makes a difference in someone's life during their most challenging times.</p>
            <Link to="/donations" className="vm-btn vm-btn--gold">Make a Donation</Link>
          </div>
          <div className="vm-support__stats">
            <Counter target={400} label="Patients Helped" />
            <Counter target={10} label="Years of Service" />
            <Counter target={3} label="Districts" />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="vm-services" id="what-we-do">
        <div className="vm-container">
          <h2 className="vm-section-title">What We Do</h2>
          <p className="vm-section-subtitle">Vidiyal Mission is committed to providing comprehensive palliative care and cancer care services in Tenkasi, Tirunelveli, and Madurai to patients and their families.</p>
          <div className="vm-services__grid">
            {[
              { icon: 'fas fa-home', title: 'Home Based Care', desc: 'Free palliative care services delivered in the comfort of patients\' homes across Tenkasi, Tirunelveli, and surrounding areas.' },
              { icon: 'fas fa-hospital', title: 'Tied Up Clinics', desc: 'Pain and symptom management clinics at major hospitals provide compassionate cancer palliative care.' },
              { icon: 'fas fa-phone', title: 'Helpline Support', desc: '24/7 telephone counseling and guidance for cancer patients and families.' },
              { icon: 'fas fa-graduation-cap', title: 'Training Programs', desc: 'Training in palliative care, counselling, community support, and resource coordination.' },
              { icon: 'fas fa-users', title: 'Bereavement Support', desc: 'Counseling and support groups for families dealing with loss.' },
              { icon: 'fas fa-heart', title: 'Peer Support', desc: 'Connecting patients and survivors for mutual support and guidance.' },
            ].map((s, i) => (
              <div className="vm-services__card" key={i}>
                <div className="vm-services__icon"><i className={s.icon}></i></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="vm-impact" id="impact">
        <div className="vm-container">
          <h2 className="vm-section-title vm-section-title--light">Our Impact</h2>
          <p className="vm-section-subtitle vm-section-subtitle--light">Making a difference in countless lives across South India through Cancer Palliative Care</p>
          <div className="vm-impact__grid">
            <Counter target={400} label="Lives Touched" />
            <Counter target={100} label="Patients Annually" />
            <Counter target={5} label="Healthcare Professionals Trained" />
            <Counter target={10} label="Volunteers" />
          </div>
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   PALLIATIVE CARE PAGE
   ════════════════════════════════════════════════════════════ */
function PalliativeCarePage() {
  const galleryImages = Array.from({ length: 8 }, (_, i) => ({
    src: `${process.env.PUBLIC_URL}/images/PAIN_ PALLIATIVECARE/PAIN_PALLIATIVECARE_${i < 4 ? i + 1 : i + 3}.jpg`,
    alt: `Cancer Palliative Care service in Tenkasi - Image ${i + 1}`,
  }));

  return (
    <>
      <SEO
        title="Pain & Palliative Care - Cancer Palliative Care in Tenkasi | Vidiyal Mission"
        description="Expert pain and palliative care for cancer patients in Tenkasi, Tirunelveli, and Madurai. Comprehensive symptom management, emotional support, and holistic care."
        keywords="pain palliative care Tenkasi, cancer palliative care, palliative care Tirunelveli, palliative care Madurai"
      />
      <PageHero src={`${process.env.PUBLIC_URL}/images/patientpallative/Pain-palliative care.jpg.jpeg`} alt="Pain and Palliative Care for Cancer Patients in Tenkasi" />

      <section className="vm-program">
        <div className="vm-container vm-program__layout">
          <div className="vm-program__images">
            <img src={`${process.env.PUBLIC_URL}/images/patientpallative/pallaitive-care-1.jpeg`} alt="Palliative care visit in Tenkasi" className="vm-program__img" />
            <img src={`${process.env.PUBLIC_URL}/images/patientpallative/pallaitive-care-2.jpeg`} alt="Cancer care support in Tirunelveli" className="vm-program__img" />
          </div>
          <div className="vm-program__content">
            <h2>What is Pain &amp; Palliative Care?</h2>
            <p>Palliative care focuses on improving the quality of life for patients with serious illnesses by managing symptoms and providing support to patients and their families. It is available at any stage of illness and can be provided alongside curative treatments. Palliative care teams include a variety of healthcare professionals who work together to address the medical, social, emotional, and practical needs of patients.</p>

            <h3>Our Key Services</h3>
            <p><strong>Pain Management:</strong> Relief from pain caused by cancer, chronic diseases, or severe injuries.</p>
            <p><strong>Symptom Management:</strong> Care for discomforts such as breathlessness, fatigue, nausea, and loss of appetite.</p>
            <p><strong>Emotional &amp; Psychological Support:</strong> Counseling for patients and families to cope with stress, anxiety, and grief.</p>
            <p><strong>Spiritual &amp; Social Care:</strong> Guidance and comfort for emotional and spiritual concerns during difficult times.</p>
            <p><strong>Holistic Care Approach:</strong> Addressing physical, emotional, mental, and social needs together.</p>

            <h3>Who Can Benefit from Our Care?</h3>
            <p>Patients with serious illnesses – cancer, heart failure, chronic kidney disease, advanced liver disease, Parkinson's, ALS, and more. Chronic pain sufferers, terminally ill patients needing end-of-life support for dignity and comfort, and families and caregivers seeking guidance, training, and emotional support.</p>

            <h3>Additional Support Services</h3>
            <p><strong>Caretaker Training:</strong> Pain management, wound care, medication administration, emotional support, and end-of-life care.</p>
            <p><strong>24/7 Virtual Support:</strong> Round-the-clock medical guidance, emergency assistance, and counseling.</p>
            <p><strong>Doctor Teleconsultation:</strong> Expert medical advice for symptom control and treatment adjustments.</p>
            <p><strong>Oxygen Provision:</strong> Assistance in arranging oxygen support for patients in need.</p>
            <p><strong>Manpower Assistance:</strong> Access to trained care assistants for hands-on patient care.</p>

            <h3>Our Commitment</h3>
            <p>At Vidiyal Mission, we believe every person deserves to live with dignity, free from unnecessary pain and distress. We walk alongside patients and their families — offering compassion, expertise, and unwavering support through every stage of the journey across Tenkasi, Tirunelveli, and Madurai.</p>
          </div>
        </div>
      </section>

      <section className="vm-gallery-section">
        <div className="vm-container">
          <ImageGallery images={galleryImages} columns={4} />
          <div className="vm-program__list">
            <p className="vm-program__intro">Professional multidisciplinary teams of doctors, counsellors, and physiotherapists advise nurses, who regularly visit registered patients in their homes, offering:</p>
            <ul>
              <li>Factual information to demystify patient's illness and its likely course in clear and understandable terms for informed decision making.</li>
              <li>Pain and symptom management aimed at maximising comfort and quality of life.</li>
              <li>Physical therapy and rehabilitation in optimising functioning ability.</li>
              <li>Comprehensive nursing care.</li>
              <li>Guidance and equipment support to achieve control and dignity in day-to-day activities.</li>
              <li>Empower and equip family caregivers in caring for their loved ones effectively at home.</li>
              <li>Psychological, emotional and spiritual support for both patients and families.</li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   HOSPICE & GERIATRIC CARE PAGE
   ════════════════════════════════════════════════════════════ */
function HospiceGeriatricCarePage() {
  const galleryImages = [
    ...Array.from({ length: 9 }, (_, i) => ({
      src: `${process.env.PUBLIC_URL}/images/HOMECARE_HOSPICECARE/HOME_CARE_HOSPICECARE_${i + 1}.jpg`,
      alt: `Home care and hospice service - Image ${i + 1}`,
    })),
    { src: `${process.env.PUBLIC_URL}/images/HOMECARE_HOSPICECARE/HOMECARE_HOSPICECARE_10.jpg`, alt: 'Hospice care 10' },
    { src: `${process.env.PUBLIC_URL}/images/HOMECARE_HOSPICECARE/HOMECARE_HOSPICECARE_11.jpg`, alt: 'Hospice care 11' },
  ];

  return (
    <>
      <SEO
        title="Home Care, Hospice & Geriatric Care - Palliative Care in Tenkasi | Vidiyal Mission"
        description="Comprehensive home care, hospice, and geriatric care services in Tenkasi and Tirunelveli. End-of-life care focused on comfort, dignity, and peace."
        keywords="hospice care Tenkasi, geriatric care, home care palliative, end of life care Tirunelveli"
      />
      <PageHero src={`${process.env.PUBLIC_URL}/images/HomeCare.jpeg`} alt="Home Care and Hospice Services" />

      <section className="vm-program">
        <div className="vm-container">
          <div className="vm-program__layout">
            <div className="vm-program__images" style={{ flex: '0 0 auto' }}>
              <img src={`${process.env.PUBLIC_URL}/images/HomeCareAndHospiceImage.jpeg`} alt="Home care and hospice" className="vm-program__img" />
            </div>
            <div className="vm-program__content">
              <h2>Home Care Services</h2>
              <p>Doctor consultations, skilled nursing care, and physiotherapy. Medication management and health monitoring. Assistance with daily activities such as bathing, dressing, and meal preparation. Emotional support and guidance for patients and families.</p>
              <h3>Who Benefits</h3>
              <p>Patients unable to travel to hospitals or clinics. Families seeking safe, convenient, and professional in-home care.</p>
            </div>
          </div>

          <div className="vm-program__content" style={{ marginTop: '2rem' }}>
            <h2>Hospice Care</h2>
            <p>Specialized end-of-life care focused on comfort, dignity, and peace for those with terminal illnesses. Our approach addresses physical, emotional, and spiritual needs for both patients and their loved ones.</p>
            <p><strong>Services:</strong> Expert pain and symptom management, emotional and psychological counseling, creating a calm and compassionate environment for the final stage of life.</p>

            <h2>Geriatric Care</h2>
            <p>Holistic, specialized care for older adults to enhance health, independence, and overall quality of life.</p>
            <p><strong>Services:</strong> Management of chronic illnesses such as diabetes, hypertension, and arthritis. Regular health check-ups, physiotherapy, and medication management. Assistance with mobility, nutrition, and hygiene. Social and emotional support to reduce loneliness and isolation.</p>
            <p>Vidiyal Medical Mission is committed to delivering care with compassion, respect, and expertise — whether at home, during life's most delicate moments, or in the golden years.</p>
          </div>
        </div>
      </section>

      <section className="vm-gallery-section">
        <div className="vm-container">
          <ImageGallery images={galleryImages} columns={4} />
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   MEDICAL CAMPS PAGE
   ════════════════════════════════════════════════════════════ */
function MedicalCampsPage() {
  const galleryImages = Array.from({ length: 4 }, (_, i) => ({
    src: `${process.env.PUBLIC_URL}/images/MEDICALCAMPNAVIGATION/MEDICALCAMPNAVIGATION-${i + 2}.jpg`,
    alt: `Medical camp - Image ${i + 1}`,
  }));

  return (
    <>
      <SEO
        title="Medical Camps & Treatment Navigation - Cancer Care in Tenkasi | Vidiyal Mission"
        description="Free medical camps and treatment navigation services in Tenkasi, Tirunelveli, and Madurai. Community health screenings, check-ups, and patient guidance."
        keywords="medical camps Tenkasi, treatment navigation, free health camps Tirunelveli, cancer screening camps"
      />
      <PageHero src={`${process.env.PUBLIC_URL}/images/Medical-Camp-Training.jpeg`} alt="Medical Camps and Treatment Navigation" />

      <section className="vm-program">
        <div className="vm-container">
          <div className="vm-program__layout">
            <div className="vm-program__images" style={{ flex: '0 0 auto' }}>
              <img src={`${process.env.PUBLIC_URL}/images/MEDICALCAMPNAVIGATION/MEDICALCAMPNAVIGATION-1.jpg`} alt="Medical camp" className="vm-program__img" />
            </div>
            <div className="vm-program__content">
              <h2>Medical Camps &amp; Treatment Navigation</h2>
              <p>At Vidiyal Medical Mission, we believe that healthcare should be accessible, affordable, and compassionate for everyone — especially for those in underserved communities. Our Medical Camps and Treatment Navigation services work hand in hand to ensure that individuals not only receive immediate care but are also guided toward the right treatment for their long-term health needs.</p>
              <h3>Medical Camps</h3>
              <p>We organize community-based medical camps where our team of doctors, nurses, and healthcare professionals offer free check-ups, screenings, and treatments. These camps are often held in rural or hard-to-reach areas, bringing essential healthcare directly to the people who need it most.</p>
            </div>
          </div>
          <div className="vm-program__content" style={{ marginTop: '2rem' }}>
            <h3>Services Offered</h3>
            <p>General health check-ups, blood pressure, sugar, and cholesterol screenings, eye and dental examinations, basic treatments for common illnesses, and health education on disease prevention and wellness.</p>
            <h3>Treatment Navigation</h3>
            <p>Healthcare can be overwhelming, especially when specialized treatment is needed. Our Treatment Navigation service helps patients find the right doctors, hospitals, and treatment plans based on their condition.</p>
            <p><strong>We Assist By:</strong> Connecting patients with the most suitable healthcare providers, arranging transportation to hospitals or clinics, explaining treatment options and available support services, and helping with insurance processes or financial assistance when needed.</p>
            <p>Our mission is simple: To bring healthcare closer to the people and guide them toward a healthier tomorrow.</p>
          </div>
        </div>
      </section>

      <section className="vm-gallery-section">
        <div className="vm-container">
          <div className="vm-highlight-box">
            <p>The Day Care program gives children, teenagers and adults with cancer and their caregivers a secure and comforting platform with opportunities for self-expression under caring individuals.</p>
          </div>
          <ImageGallery images={galleryImages} columns={4} />
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   HEALTH EDUCATION PAGE
   ════════════════════════════════════════════════════════════ */
function HealthEducationPage() {
  const galleryImages = [
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (1).jpeg`, alt: 'Health education 1' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (2).jpeg`, alt: 'Health education 2' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (3).jpeg`, alt: 'Health education 3' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (4).jpg`, alt: 'Health education 4' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (5).jpg`, alt: 'Health education 5' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (6).jpg`, alt: 'Health education 6' },
    { src: `${process.env.PUBLIC_URL}/images/HealthEducation/Health education (7).jpg`, alt: 'Health education 7' },
  ];

  return (
    <>
      <SEO
        title="Health Education, Training & Awareness - Palliative Care Training Tenkasi | Vidiyal Mission"
        description="Health education, BLS training, first aid, palliative care training, and disease awareness programs in Tenkasi, Tirunelveli, and Madurai communities."
        keywords="health education Tenkasi, palliative care training, BLS training, first aid training Tirunelveli"
      />
      <PageHero src={`${process.env.PUBLIC_URL}/images/Health-Education.jpeg`} alt="Health Education, Training and Awareness" />

      <section className="vm-program">
        <div className="vm-container">
          <div className="vm-program__layout">
            <div className="vm-program__images" style={{ flex: '0 0 auto' }}>
              <img src={`${process.env.PUBLIC_URL}/images/HealthEducation/Health education (7).jpg`} alt="Health education" className="vm-program__img" />
            </div>
            <div className="vm-program__content">
              <h2>Health Education, Training &amp; Awareness</h2>
              <p>At Vidiyal Medical Mission, we believe that knowledge is the first step toward saving lives and creating healthier communities. We provide customized training programs for students, communities, government and private sector employees, and caregivers.</p>

              <h3>Basic Life Support (BLS) Training</h3>
              <p>BLS includes essential life-saving techniques such as CPR and emergency interventions. Students learn vital emergency skills, communities gain the ability to respond quickly to heart attacks and strokes, and caregivers receive specialized training in airway management and emergency palliative care.</p>

              <h3>First Aid Management Training</h3>
              <p>First aid is the immediate care given to an injured or ill person before professional help arrives. Students acquire practical skills to manage burns, cuts, sprains, and other common injuries.</p>

              <h3>Palliative Care Training</h3>
              <p>Focuses on improving quality of life for people with serious, chronic, or life-limiting illnesses. Communities learn practical skills to support loved ones at home, easing pain and improving comfort.</p>

              <h3>Common and Pandemic Disease Training</h3>
              <p>Empowering students, volunteers, and citizens to become proactive health guardians by providing knowledge and skills to prevent, detect, and respond to infectious diseases.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="vm-gallery-section">
        <div className="vm-container">
          <div className="vm-highlight-box">
            <p>At Vidiyal Medical Mission, we are committed to creating a healthier, more responsive society — one training session at a time. By empowering people with knowledge and practical skills, we help transform concern into action, and action into better health for all.</p>
          </div>
          <ImageGallery images={galleryImages} columns={4} />
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   TEAMS PAGE
   ════════════════════════════════════════════════════════════ */
function TeamsPage() {
  const teams = {
    'Board of Trustees': [
      { name: 'Mr. Mani Sekaran', role: 'Foundation', img: 'Mani Sekaran.jpg.jpeg' },
      { name: 'Mr. Jaba Singraj', role: 'Managing', img: 'Jaba Singraj.jpg.jpeg' },
      { name: 'Ms. Jerald Antro Gnanam', role: 'Financing Division', img: 'Jerald.jpg.jpeg' },
      { name: 'Mr. Vairamuthu', role: 'Trustee', img: 'Vaira muthu.jpg.jpeg' },
    ],
    'Medical Services': [
      { name: 'Dr. Indhuja', role: 'Medical Oncologist', img: 'Dr. Indhuja.jpg.jpeg' },
      { name: 'Dr. Surender', role: 'General Physician', img: 'Dr. Surender.png' },
      { name: 'Dr. Lisma Mano', role: 'General Physician', img: 'Dr. Lishma.jpg.jpeg' },
    ],
    'Physiotherapy': [
      { name: 'Dr. S. Ganesan', role: 'Sports Physiotherapy', img: 'Dr. Ganesan.jpg.jpeg' },
      { name: 'Dr. Paul Alex Daniel', role: 'BPT', img: 'Dr. Paul Alex Daniel.jpg.jpeg' },
      { name: 'Dr. Selvan Perinbaraj', role: 'BPT', img: 'Dr. Selvan Perinbaraj.jpg.jpeg' },
      { name: 'Dr. Selva Kumar', role: 'BPT', img: 'Dr. Selva Kumar.jpg.jpeg' },
      { name: 'Dr. Samuel Prem Kumar', role: 'BPT', img: 'Dr. Samuel Prem kumar.png' },
    ],
    'Advisory': [
      { name: 'Mr. Immanual Vasu', role: 'Advisory', img: 'Immanuel Vasu.jpg.jpeg' },
      { name: 'Mr. Anandan', role: 'Advisory', img: 'Ananadan.jpg.jpeg' },
      { name: 'Mr. Esakki', role: 'Advisory', img: 'Isakki.jpg.jpeg' },
      { name: 'Mr. Dinakaran', role: 'Board Member', img: 'Dhinakar.jpg.jpeg' },
      { name: 'Mr. Joseph Raj', role: 'Advisory', img: 'Joseph.jpg.jpeg' },
    ],
  };

  return (
    <>
      <SEO
        title="Our Team - Vidiyal Medical Mission | Cancer Palliative Care Team in Tenkasi"
        description="Meet the dedicated team of doctors, physiotherapists, trustees, and advisors behind Vidiyal Medical Mission's palliative care services in Tenkasi and Tirunelveli."
        keywords="palliative care team Tenkasi, cancer care doctors, physiotherapy team Tirunelveli"
      />
      <section className="vm-teams">
        <h2 className="vm-section-title vm-section-title--light">Structure of Team</h2>
        <h3 className="vm-teams__subtitle">Managing Committee</h3>
        <div className="vm-teams__grid">
          {Object.entries(teams).map(([dept, members]) => (
            <div className="vm-teams__col" key={dept}>
              <h4 className="vm-teams__dept">{dept}</h4>
              {members.map((m, i) => (
                <div className="vm-teams__card" key={i}>
                  <img src={`${process.env.PUBLIC_URL}/images/teams/${m.img}`} alt={m.name} loading="lazy" />
                  <div>
                    <strong>{m.name}</strong>
                    <span>{m.role}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   DONATIONS PAGE
   ════════════════════════════════════════════════════════════ */
function DonationsPage() {
  return (
    <>
      <SEO
        title="Donate to Vidiyal Medical Mission - Support Cancer Palliative Care in Tenkasi"
        description="Donate to support free palliative care, hospice services, and medical support for cancer patients in Tenkasi, Tirunelveli, and Madurai. 50% tax exempt under 80G."
        keywords="donate palliative care, cancer care donation Tenkasi, charity donation Tirunelveli"
      />
      <section className="vm-donations">
        <div className="vm-container">
          <div className="vm-donations__bank">
            <h2>Account Details</h2>
            <div className="vm-donations__row"><span className="vm-donations__label">ACCOUNT NAME:</span><span className="vm-donations__value">VIDIYAL MEDICAL MISSION</span></div>
            <div className="vm-donations__row"><span className="vm-donations__label">BANK NAME:</span><span className="vm-donations__value">CANARA BANK</span></div>
            <div className="vm-donations__row"><span className="vm-donations__label">BRANCH:</span><span className="vm-donations__value">GUNARAMANALLUR</span></div>
            <div className="vm-donations__row"><span className="vm-donations__label">ACCOUNT NO:</span><span className="vm-donations__value">3656101007963</span></div>
            <div className="vm-donations__row"><span className="vm-donations__label">IFSC CODE:</span><span className="vm-donations__value">CNRB0003656</span></div>
            <div className="vm-donations__row"><span className="vm-donations__label">MICR CODE:</span><span className="vm-donations__value">627015049</span></div>
          </div>

          <div className="vm-donations__qr">
            <img src={`${process.env.PUBLIC_URL}/images/qrcode.jpeg`} alt="QR Code for donation" />
          </div>

          <div className="vm-donations__content">
            <p>Your support helps us provide compassionate palliative care, hospice services, and essential medical support to cancer patients in need across Tenkasi, Tirunelveli, and Madurai.</p>

            <h3>Online Payment (Recommended)</h3>
            <p>The easiest way to donate is through our secure payment gateway. Supports Credit Cards, Debit Cards, Net Banking (all major Indian banks), and Mobile Wallets.</p>

            <h3>Direct Bank Transfer (INR Account)</h3>
            <p>Indian donors, including NRIs with Indian accounts, can donate directly via bank transfer. Alternatively, you can mail the cheque to Vidiyal Mission, 279, Amman Sannathi 2nd St, Near Kasiviswanathar Temple, Tenkasi, South India - 627811.</p>
            <p>Contact: +91-9486849191 / +91-9894848252</p>
            <p>Email: <a href="mailto:contactus@vidiyalmedicalmission.org">contactus@vidiyalmedicalmission.org</a></p>

            <h3>Cheque Donation</h3>
            <p>You can donate via cheque in favour of "Vidiyal Medical Mission". Within Tenkasi: Pickup can be arranged. Contact: +91-9894848252</p>

            <p>For queries/clarifications please contact our donor relations team at <a href="mailto:contactus@vidiyalmedicalmission.org">contactus@vidiyalmedicalmission.org</a></p>

            <div className="vm-donations__sig">
              <p>Regards,</p>
              <p>Donor Relations Team</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   LAYOUT WRAPPER
   ════════════════════════════════════════════════════════════ */
function Layout({ children }) {
  return (
    <>
      <TopBar />
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   APP
   ════════════════════════════════════════════════════════════ */
export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/palliative-care" element={<PalliativeCarePage />} />
          <Route path="/hospice-geriatric-care" element={<HospiceGeriatricCarePage />} />
          <Route path="/medical-camps" element={<MedicalCampsPage />} />
          <Route path="/health-education" element={<HealthEducationPage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/donations" element={<DonationsPage />} />
        </Routes>
      </Layout>
    </Router>
  );
}
